import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { getEquipment } from "../utils/equipmentUtils";
import CustomButton from "./common/CustomButton";
import CustomInput from "./common/CustomInput";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { CalendarIcon, CurrencyDollarIcon } from "@heroicons/react/24/outline";

Modal.setAppElement("#root");

const styles = {
  modal: {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      width: "100%",
      maxWidth: 600,
      maxHeight: "80vh",
      borderRadius: 12,
      padding: 16,
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.tertiary}`,
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      animation: "fadeIn 0.5s ease-in-out",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
    },
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
    marginBottom: 12,
  },
  select: {
    width: "100%",
    height: 56,
    padding: "0 12px",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    marginBottom: 12,
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  datePickerContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: COLORS.white,
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    marginBottom: 12,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: COLORS.primary,
    },
  },
  dateRow: {
    display: "flex",
    gap: 12,
  },
  dateCol: {
    flex: 1,
    minWidth: 0,
  },
  datePickerText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
    marginLeft: 48,
    flex: 1,
  },
  datePicker: {
    width: "100%",
    padding: 12,
    border: "none",
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: "transparent",
    outline: "none",
  },
  priceRangeContainer: {
    display: "flex",
    gap: 12,
    marginBottom: 12,
  },
  priceInput: {
    flex: 1,
  },
  buttonRow: {
    display: "flex",
    gap: 8,
    marginTop: 12,
  },
  applyButton: {
    flex: 1,
    borderRadius: 12,
  },
  clearButton: {
    flex: 1,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.tertiary}`,
    borderRadius: 12,
  },
  clearButtonText: {
    color: COLORS.text,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.tertiary}`,
    borderRadius: 12,
  },
  cancelButtonText: {
    color: COLORS.text,
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    modal: {
      content: {
        width: "95%",
        maxWidth: 400,
      },
    },
    title: {
      fontSize: 28,
    },
    select: {
      height: 48,
    },
    datePickerContainer: {
      padding: 10,
    },
    datePickerText: {
      fontSize: 14,
    },
    datePicker: {
      padding: 10,
      fontSize: 14,
    },
  },
};

const OrderFilterModal = ({
  isVisible,
  onClose,
  onApply,
  currentFilters,
  userId,
}) => {
  const [equipmentList, setEquipmentList] = useState([]);
  const [filters, setFilters] = useState({
    equipmentId: currentFilters.equipmentId || "",
    startDateFrom: currentFilters.startDateFrom || null,
    startDateTo: currentFilters.startDateTo || null,
    endDateFrom: currentFilters.endDateFrom || null,
    endDateTo: currentFilters.endDateTo || null,
    priceMin: currentFilters.priceMin || "",
    priceMax: currentFilters.priceMax || "",
    status: currentFilters.status || "",
  });

  const statusOptions = ["Pending", "Active", "Completed", "Cancelled"];

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const list = await getEquipment(userId);
        setEquipmentList(list);
      } catch (error) {
        console.error("Fetch equipment error:", error);
      }
    };
    fetchEquipment();
  }, [userId]);

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = {
      equipmentId: "",
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
      priceMin: "",
      priceMax: "",
      status: "",
    };
    setFilters(clearedFilters);
    onApply(clearedFilters);
    onClose();
  };

  return (
    <Modal isOpen={isVisible} onRequestClose={onClose} style={styles.modal}>
      <div>
        <h2 style={styles.title}>Filter Orders</h2>
        <select
          value={filters.equipmentId}
          onChange={(e) =>
            setFilters({ ...filters, equipmentId: e.target.value })
          }
          style={styles.select}
        >
          <option value="">All equipment</option>
          {equipmentList.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={styles.select}
        >
          <option value="">All statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div style={styles.dateRow}>
          <div style={{ ...styles.datePickerContainer, ...styles.dateCol }}>
            <CalendarIcon width={20} height={20} stroke={COLORS.primary} />
            <DatePicker
              selected={filters.startDateFrom}
              onChange={(date) =>
                setFilters({ ...filters, startDateFrom: date })
              }
              dateFormat="MM/dd/yyyy"
              placeholderText="Start Date From"
              className="date-picker"
            />
          </div>
          <div style={{ ...styles.datePickerContainer, ...styles.dateCol }}>
            <CalendarIcon width={20} height={20} stroke={COLORS.primary} />
            <DatePicker
              selected={filters.startDateTo}
              onChange={(date) => setFilters({ ...filters, startDateTo: date })}
              dateFormat="MM/dd/yyyy"
              placeholderText="Start Date To"
              className="date-picker"
            />
          </div>
        </div>
        <div style={styles.dateRow}>
          <div style={{ ...styles.datePickerContainer, ...styles.dateCol }}>
            <CalendarIcon width={20} height={20} stroke={COLORS.primary} />
            <DatePicker
              selected={filters.endDateFrom}
              onChange={(date) => setFilters({ ...filters, endDateFrom: date })}
              dateFormat="MM/dd/yyyy"
              placeholderText="End Date From"
              className="date-picker"
            />
          </div>
          <div style={{ ...styles.datePickerContainer, ...styles.dateCol }}>
            <CalendarIcon width={20} height={20} stroke={COLORS.primary} />
            <DatePicker
              selected={filters.endDateTo}
              onChange={(date) => setFilters({ ...filters, endDateTo: date })}
              dateFormat="MM/dd/yyyy"
              placeholderText="End Date To"
              className="date-picker"
            />
          </div>
        </div>
        <div style={styles.priceRangeContainer}>
          <CustomInput
            value={filters.priceMin}
            onChangeText={(value) =>
              setFilters({ ...filters, priceMin: value })
            }
            icon={
              <CurrencyDollarIcon
                width={24}
                height={24}
                stroke={COLORS.primary}
              />
            }
            placeholder="Min Price"
            type="number"
            style={styles.priceInput}
          />
          <CustomInput
            value={filters.priceMax}
            onChangeText={(value) =>
              setFilters({ ...filters, priceMax: value })
            }
            icon={
              <CurrencyDollarIcon
                width={24}
                height={24}
                stroke={COLORS.primary}
              />
            }
            placeholder="Max Price"
            type="number"
            style={styles.priceInput}
          />
        </div>
        <div style={styles.buttonRow}>
          <CustomButton
            title="Cancel"
            onPress={onClose}
            style={styles.cancelButton}
            textStyle={styles.cancelButtonText}
          />
          <CustomButton
            title="Clear"
            onPress={handleClear}
            style={styles.clearButton}
            textStyle={styles.clearButtonText}
          />
          <CustomButton
            title="Apply"
            onPress={handleApply}
            style={styles.applyButton}
          />
        </div>
      </div>
    </Modal>
  );
};

export default OrderFilterModal;
